import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Download, Upload, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axiosInstance from '../../api/axios';

/**
 * ProductSyncDialog — Preview → Prod (oder umgekehrt) Produkt-Sync via JSON.
 *
 * Workflow:
 *   1. Preview-Umgebung: „📤 JSON exportieren" → Datei speichern
 *   2. Prod-Umgebung: Selbe Datei hochladen → Vorschau → „Übernehmen"
 *
 * Modi:
 *   - Upsert (Standard): fügt neue hinzu, aktualisiert bestehende
 *   - Insert-only: überspringt bestehende (kein Overwrite)
 *   - Replace-all: LÖSCHT alle bestehenden, dann Import (gefährlich, mit Doppel-Bestätigung)
 */
export default function ProductSyncDialog({ isOpen, onClose, onSyncComplete }) {
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState('upsert');
  const fileInputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setMode('upsert');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // --------- Export ---------
  const handleExport = async () => {
    try {
      setBusy(true);
      const res = await axiosInstance.get('/products/admin/export-json');
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `homeyoursoul-produkte-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${res.data.count} Produkte als JSON exportiert.`);
    } catch (e) {
      toast.error('Export fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  // --------- Import: file → preview ---------
  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
    try {
      setBusy(true);
      const text = await f.text();
      const parsed = JSON.parse(text);
      const products = parsed.products || parsed;
      if (!Array.isArray(products) || products.length === 0) {
        toast.error('Datei enthält keine Produkte');
        setFile(null);
        return;
      }
      const res = await axiosInstance.post('/products/admin/import-json', {
        products,
        mode,
        dry_run: true,
      });
      setPreview({ ...res.data, _products: products });
    } catch (e) {
      toast.error('Datei konnte nicht gelesen werden: ' + (e?.response?.data?.detail || e.message));
      setFile(null);
    } finally {
      setBusy(false);
    }
  };

  // --------- Re-preview when mode changes ---------
  const rerunPreview = async (newMode) => {
    if (!preview?._products) return;
    setMode(newMode);
    try {
      setBusy(true);
      const res = await axiosInstance.post('/products/admin/import-json', {
        products: preview._products,
        mode: newMode,
        dry_run: true,
      });
      setPreview({ ...res.data, _products: preview._products });
    } catch (e) {
      toast.error('Vorschau fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  // --------- Import: execute ---------
  const handleExecute = async () => {
    if (!preview?._products) return;

    if (!window.confirm(
      `${preview.would_insert} neu hinzufügen und ${preview.would_update} aktualisieren?\n\n${preview.would_skip} werden übersprungen.\n\nDiese Aktion überschreibt bestehende Produkte, löscht aber KEINE.`
    )) return;

    try {
      setBusy(true);
      const res = await axiosInstance.post('/products/admin/import-json', {
        products: preview._products,
        mode,
        dry_run: false,
      });
      toast.success(res.data.message || 'Sync abgeschlossen');
      onSyncComplete?.();
      handleClose();
    } catch (e) {
      toast.error('Import fehlgeschlagen: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="product-sync-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#5D4E3C]">
            🔄 Preview ↔ Prod Sync
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Erklärung */}
          <div className="bg-[#FAF5EE] border border-[#D4C4B0] p-3 text-sm text-[#5D4E3C]">
            <p className="font-semibold mb-1">Workflow:</p>
            <ol className="list-decimal list-inside space-y-0.5 text-xs">
              <li>In <strong>Preview</strong>: JSON exportieren → Datei speichern</li>
              <li>In <strong>Prod</strong> (homeyoursoul.com): Selbe Datei hier hochladen</li>
              <li>Vorschau prüfen → &bdquo;Übernehmen&ldquo; klicken</li>
            </ol>
          </div>

          {/* EXPORT */}
          <div className="border border-[#D4C4B0] p-4 bg-white">
            <h3 className="text-sm font-semibold text-[#5D4E3C] mb-2 flex items-center gap-2">
              <Download className="h-4 w-4" /> 1. Export (aus dieser Umgebung)
            </h3>
            <p className="text-xs text-gray-600 mb-3">
              Alle Produkte inkl. Bildpfade, Kategorien, Preise, SKUs & Farbvarianten als JSON-Datei herunterladen.
            </p>
            <Button
              onClick={handleExport}
              disabled={busy}
              className="rounded-none bg-[#8B6F4D] hover:bg-[#5D4E3C] text-white"
              data-testid="product-sync-export-btn"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
              Als JSON exportieren
            </Button>
          </div>

          {/* IMPORT */}
          <div className="border border-[#D4C4B0] p-4 bg-white">
            <h3 className="text-sm font-semibold text-[#5D4E3C] mb-2 flex items-center gap-2">
              <Upload className="h-4 w-4" /> 2. Import (in diese Umgebung)
            </h3>
            <p className="text-xs text-gray-600 mb-3">
              JSON-Datei aus vorherigem Export hochladen. Es folgt eine Vorschau mit den geplanten Änderungen.
            </p>

            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              onChange={handleFileChange}
              className="text-xs border border-[#D4C4B0] bg-[#FAF5EE] p-2 w-full"
              data-testid="product-sync-file-input"
              disabled={busy}
            />

            {file && (
              <p className="text-xs text-gray-500 mt-2">
                Ausgewählt: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(1)} KB)
              </p>
            )}

            {/* Modus */}
            {preview && (
              <div className="mt-4 space-y-3">
                <div>
                  <label className="text-xs font-semibold text-[#5D4E3C] block mb-1">Modus:</label>
                  <div className="grid grid-cols-1 gap-2 text-xs">
                    <label className="flex items-start gap-2 border border-[#D4C4B0] p-2 cursor-pointer hover:bg-[#FAF5EE]">
                      <input type="radio" name="mode" value="upsert" checked={mode === 'upsert'} onChange={(e) => rerunPreview(e.target.value)} className="mt-0.5" data-testid="mode-upsert" />
                      <div>
                        <p className="font-semibold text-[#5D4E3C]">Upsert (empfohlen)</p>
                        <p className="text-gray-600">Neue hinzufügen, bestehende (per SKU/Name) aktualisieren.</p>
                      </div>
                    </label>
                    <label className="flex items-start gap-2 border border-[#D4C4B0] p-2 cursor-pointer hover:bg-[#FAF5EE]">
                      <input type="radio" name="mode" value="insert_only" checked={mode === 'insert_only'} onChange={(e) => rerunPreview(e.target.value)} className="mt-0.5" data-testid="mode-insert-only" />
                      <div>
                        <p className="font-semibold text-[#5D4E3C]">Nur neue einfügen</p>
                        <p className="text-gray-600">Bestehende bleiben unverändert (kein Overwrite).</p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Preview stats */}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-green-50 border border-green-200 p-2">
                    <p className="text-2xl font-bold text-green-700" data-testid="preview-insert-count">{preview.would_insert}</p>
                    <p className="text-[10px] text-green-800 uppercase tracking-wide">Neu</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 p-2">
                    <p className="text-2xl font-bold text-blue-700" data-testid="preview-update-count">{preview.would_update}</p>
                    <p className="text-[10px] text-blue-800 uppercase tracking-wide">Aktualisiert</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 p-2">
                    <p className="text-2xl font-bold text-gray-700" data-testid="preview-skip-count">{preview.would_skip}</p>
                    <p className="text-[10px] text-gray-800 uppercase tracking-wide">Übersprungen</p>
                  </div>
                </div>

                {/* Samples */}
                {preview.sample_insert?.length > 0 && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-[#8B6F4D] font-semibold">Neue Produkte (Vorschau)</summary>
                    <ul className="mt-1 space-y-0.5 pl-3 max-h-32 overflow-y-auto">
                      {preview.sample_insert.map((p, i) => (
                        <li key={i} className="text-gray-600">• {p.sku || '—'} · {p.name} <span className="text-gray-400">({p.category})</span></li>
                      ))}
                    </ul>
                  </details>
                )}

                {preview.sample_update?.length > 0 && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-[#8B6F4D] font-semibold">Zu aktualisieren (Vorschau)</summary>
                    <ul className="mt-1 space-y-0.5 pl-3 max-h-32 overflow-y-auto">
                      {preview.sample_update.map((p, i) => (
                        <li key={i} className="text-gray-600">• {p.sku || '—'} · {p.name} <span className="text-gray-400">({p.matched_by})</span></li>
                      ))}
                    </ul>
                  </details>
                )}

                <Button
                  onClick={handleExecute}
                  disabled={busy || (preview.would_insert === 0 && preview.would_update === 0)}
                  className="w-full rounded-none bg-[#8B6F4D] hover:bg-[#5D4E3C] text-white"
                  data-testid="product-sync-execute-btn"
                >
                  {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                  Übernehmen
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
